import {ChangeEvent, useCallback, useEffect, useState} from 'react';
import {useParams} from 'react-router-dom';
import {
  Box,
  Button,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import {useRqGetDemoStingerById} from '../../../../react_query/demoStingerQuery';
import DemoStingerAddAudio from './DemoStingerAddAudio';
import DemoStingerContent from './DemoStingerContent';
import {
  IDemoScriptStateProp,
  IDemoStingerMessageProps,
} from '../../../../dto/demoStingerFulfilment.dto';
import {apiPost} from '../../../../axiosConfig';
import DialogApiRequestStatus from '../../../../components/dialogs/DialogApiRequestStatus';
import UploadFiles from '../../../../components/styles/UploadFiles';
import DemoStingerAdSpot from './DemoStingerAdSpot';
import DemoStingerAdScriptView from './DemoStingerAdScriptView';
import DemoPartnerStinger from './DemoPartnerStinger';
import {broadCastCloseTab} from '../../../../react_query/tasks/ListTasks';

const DemoStingerAddVoice = () => {
  const task = useParams();
  const taskId = Number(task.task_id);
  const {data, isError, isFetched} = useRqGetDemoStingerById(taskId);
  const [message, setMessage] = useState<IDemoScriptStateProp[]>([]);
  const [apiState, setApiState] = useState({status: '', error: null});
  const [files, setFiles] = useState<File[]>([]);
  const [name, setName] = useState('');
  useEffect(() => {
    if (data && !isError && isFetched) {
      const scripts: IDemoScriptStateProp[] = [];
      data.messages.forEach((val: IDemoStingerMessageProps) => {
        scripts.push({
          id: val.id,
          name: val.name,
          message: val.message,
          file: null,
          file_url: val.file_url,
        });
      });
      setMessage(scripts);
    }
  }, [data, isError, isFetched]);
  const isButtonDisabled = message.some(msg => msg.file === null);
  const handleSubmit = useCallback(() => {
    setApiState({
      status: 'pending',
      error: null,
    });
    const fd = new FormData();
    const id = data?.values.id?.toString() || '';
    if (id !== undefined) {
      fd.append('demo_stinger_fulfilment_id', id.toString());
    }
    message.forEach((val, index) => {
      if (val.id && val.file) {
        fd.append(`scripts[${index}][id]`, val.id.toString());
        fd.append(`scripts[${index}][file]`, val.file);
      }
    });
    apiPost(`/v1/task/${taskId}/demo-stinger/file`, fd)
      .then(() => {
        setApiState({
          status: 'success',
          error: null,
        });
      })
      .catch(error => {
        setApiState({
          status: 'error',
          error,
        });
      });
  }, [data?.values.id, message, taskId]);
  const handleEdit = useCallback(() => {
    setApiState({
      status: '',
      error: null,
    });
  }, []);
  const handleCancel = useCallback(() => {
    broadCastCloseTab();
  }, []);
  const handleFinalFileSubmit = useCallback(() => {
    const requestType = data?.values?.request_type;
    const id = data?.values?.id?.toString() || '';
    if (!id || files.length === 0 || !name) return;

    setApiState({status: 'pending', error: null});

    const finalFD = new FormData();
    const scriptFD = new FormData();
    finalFD.append('demo_stinger_fulfilment_id', id);
    finalFD.append('name', name);
    files.forEach((file, index) => {
      finalFD.append(`file[${index}]`, file);
      scriptFD.append(`scripts[${index}][file]`, file);
    });

    // --- API Calls ---
    const uploadFinalFile = () =>
      apiPost(`/v1/task/${taskId}/demo-stinger/final-file`, finalFD);

    const uploadScriptFiles = () => {
      scriptFD.append('demo_stinger_fulfilment_id', id);

      files.forEach((file, index) => {
        scriptFD.append(`scripts[${index}][file]`, file);
      });

      message.forEach((val, index) => {
        if (val.id) {
          scriptFD.append(`scripts[${index}][id]`, val.id.toString());
        }
      });

      return apiPost(`/v1/task/${taskId}/demo-stinger/file`, scriptFD);
    };

    // --- Conditional Execution ---
    const uploadSequence =
      requestType === 5
        ? uploadScriptFiles().then(() => uploadFinalFile())
        : uploadFinalFile();

    uploadSequence
      .then(() => setApiState({status: 'success', error: null}))
      .catch(error => setApiState({status: 'error', error}));
  }, [
    data?.values?.id,
    data?.values?.request_type,
    files,
    name,
    message,
    taskId,
  ]);

  const handleName = (e: ChangeEvent<HTMLInputElement>) => {
    setName(e.target.value);
  };
  return (
    <Card className='m-2'>
      <CardContent className='p-10'>
        {data && !isError && isFetched && (
          <DemoStingerContent values={data.values} />
        )}
        <Typography variant='h5' className='text-white'>
          Upload Final File
        </Typography>
        {data?.values.task_slug === 'demo-stinger-production' && (
          <TableContainer>
            <Table
              sx={{
                '.MuiTableCell-root, .MuiTableRow-root': {
                  border: 'none !important',
                  padding: '6px 0',
                },
              }}>
              <TableBody>
                <TableRow>
                  <TableCell style={{width: '260px'}}>
                    <Typography>Message Name </Typography>
                    <TextField
                      variant='outlined'
                      onChange={handleName}></TextField>
                  </TableCell>
                  <TableCell
                    className='px-2'
                    align='center'
                    style={{width: '560px'}}>
                    <UploadFiles
                      setFiles={setFiles}
                      files={files}
                      max={1}
                      height={150}
                      width={250}
                    />
                  </TableCell>
                  <TableCell>
                    <Button
                      variant='contained'
                      className='flex justify-center'
                      disabled={files.length === 0 || !name}
                      onClick={handleFinalFileSubmit}>
                      Save & Upload
                    </Button>
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </TableContainer>
        )}
        {data?.values.task_slug !== 'demo-fulfilment-nr' ? (
          <DemoStingerAddAudio
            message={message}
            setMessage={setMessage}
            taskSlug={data?.values.task_slug}
            requestType={data?.values.request_type}
          />
        ) : (
          <DemoStingerAdSpot />
        )}
        {data?.values?.task_slug === 'demo-stinger-production' && (
          <>
            {data?.adScript && Boolean(data?.adScript.length) && (
              <DemoStingerAdScriptView adScript={data.adScript} />
            )}
            {data?.partnerStinger && Boolean(data?.partnerStinger.length) && (
              <DemoPartnerStinger partnerStinger={data.partnerStinger} />
            )}
          </>
        )}
        {data?.values.task_slug !== 'demo-stinger-production' &&
          data?.values.task_slug !== 'demo-fulfilment-nr' && (
            <Box className='flex justify-center p-10'>
              <Button
                variant='contained'
                disabled={isButtonDisabled}
                onClick={handleSubmit}>
                Submit
              </Button>
            </Box>
          )}
      </CardContent>
      {apiState.status !== '' && (
        <DialogApiRequestStatus
          onEdit={handleEdit}
          onRetry={
            data?.values.task_slug === 'demo-stinger-production'
              ? handleFinalFileSubmit
              : handleSubmit
          }
          onClose={handleCancel}
          apiState={apiState}
        />
      )}
    </Card>
  );
};

export default DemoStingerAddVoice;
